import { useSyncExternalStore, useMemo } from "react";
import { authStore } from "./auth-store";
import {
  tabsApi,
  type ApiTab,
  type ApiTabItem,
  type ApiTabPayment,
} from "./api/client";

export type TabItem = ApiTabItem;
export type TabPayment = ApiTabPayment;
export type Tab = ApiTab;

type TabState = {
  tabs: Tab[];
};

const initial: TabState = {
  tabs: [],
};

let state: TabState = initial;
let version = 0;
const listeners = new Set<() => void>();

function persist() {
  version++;
  listeners.forEach((l) => l());
}

function set(updater: (s: TabState) => TabState) {
  state = updater(state);
  version++;
  persist();
}

/** Fetch tabs from API */
async function fetchMyTabs() {
  try {
    const tabs = await tabsApi.listMy();
    set((s) => ({ ...s, tabs }));
  } catch {
    // Silent fail
  }
}

async function fetchAllTabs() {
  try {
    const tabs = await tabsApi.listAll();
    set((s) => ({ ...s, tabs }));
  } catch {
    // Silent fail
  }
}

async function fetchWaiterTabs() {
  try {
    const tabs = await tabsApi.listWaiter();
    set((s) => ({ ...s, tabs }));
  } catch {
    // Silent fail
  }
}

export const tabStore = {
  getState: () => state,
  subscribe: (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },

  /** Initialize - load tabs based on user role */
  async init() {
    await fetchMyTabs();
  },

  async initStaff() {
    await fetchAllTabs();
  },

  async initWaiter() {
    await fetchWaiterTabs();
  },

  /** Get the current open tab for the logged-in viewer, or null. */
  getCurrentTab(): Tab | null {
    const user = authStore.getState().user;
    if (!user) return null;
    return (
      state.tabs.find((t) => t.customerId === user.id && t.status === "open") ??
      null
    );
  },

  /** Get all tabs for the logged-in user (both open and closed). */
  getUserTabs(): Tab[] {
    const user = authStore.getState().user;
    if (!user) return [];
    return state.tabs.filter((t) => t.customerId === user.id);
  },

  /** Create a new open tab for the logged-in user. */
  async createTab(): Promise<Tab | null> {
    try {
      const tab = await tabsApi.create();
      set((s) => ({ ...s, tabs: [tab, ...s.tabs] }));
      return tab;
    } catch {
      return null;
    }
  },

  /** Add items to the current open tab. Creates a tab if none exists. */
  async addToTab(
    itemId: string,
    name: string,
    qty: number,
    unitPrice: number,
    serverName?: string,
  ): Promise<Tab | null> {
    const user = authStore.getState().user;
    if (!user) return null;

    let tab = state.tabs.find(
      (t) => t.customerId === user.id && t.status === "open",
    );

    if (!tab) {
      const created = await tabsApi.create();
      if (!created) return null;
      tab = created;
      set((s) => ({ ...s, tabs: [created, ...s.tabs] }));
    }

    try {
      await tabsApi.addItem(tab.id, {
        itemId,
        name,
        qty,
        unitPrice,
      });
      // Refresh tabs
      await fetchMyTabs();
      return tabStore.getCurrentTab();
    } catch {
      return null;
    }
  },

  /** Record a payment against the current open tab. */
  async payTab(
    amount: number,
    method: TabPayment["method"],
  ): Promise<Tab | null> {
    const tab = tabStore.getCurrentTab();
    if (!tab) return null;
    try {
      await tabsApi.pay(tab.id, amount, method);
      await fetchMyTabs();
      return tabStore.getCurrentTab();
    } catch {
      return null;
    }
  },

  /** Close the current open tab. */
  async closeTab(): Promise<Tab | null> {
    const tab = tabStore.getCurrentTab();
    if (!tab) return null;
    try {
      await tabsApi.close(tab.id);
      await fetchMyTabs();
      return null;
    } catch {
      return null;
    }
  },

  /** Get all open tabs across all customers (for staff). */
  getAllOpenTabs(): Tab[] {
    return state.tabs.filter((t) => t.status === "open");
  },

  // ── Staff methods ──────────────────────────────────────────────────────

  /** Staff creates a new tab for a walk-in customer */
  async staffCreateTab(customerName: string): Promise<Tab | null> {
    try {
      const tab = await tabsApi.create(customerName);
      set((s) => ({ ...s, tabs: [tab, ...s.tabs] }));
      return tab;
    } catch {
      return null;
    }
  },

  // ── Waiter methods ──────────────────────────────────────────────────────

  /** Get all open tabs assigned to the logged-in waiter */
  getWaiterTabs(): Tab[] {
    const user = authStore.getState().user;
    if (!user) return [];
    return state.tabs.filter(
      (t) => t.waiterId === user.id && t.status === "open",
    );
  },

  /** Waiter creates a new tab for a walk-in customer */
  async waiterCreateTab(customerName: string): Promise<Tab | null> {
    try {
      const tab = await tabsApi.waiterCreate(customerName);
      set((s) => ({ ...s, tabs: [tab, ...s.tabs] }));
      return tab;
    } catch {
      return null;
    }
  },

  /**
   * Waiter adds multiple items to their own tab in one batch.
   */
  async waiterBatchAddToTab(
    tabId: string,
    lines: { itemId: string; name: string; qty: number; unitPrice: number }[],
  ): Promise<Tab | null> {
    try {
      await tabsApi.batchAddItems(tabId, lines);
      await fetchWaiterTabs();
      return state.tabs.find((t) => t.id === tabId) ?? null;
    } catch {
      return null;
    }
  },

  /** Waiter adds items to their own tab (single line) */
  async waiterAddToTab(
    tabId: string,
    itemId: string,
    name: string,
    qty: number,
    unitPrice: number,
  ): Promise<Tab | null> {
    return tabStore.waiterBatchAddToTab(tabId, [
      { itemId, name, qty, unitPrice },
    ]);
  },

  /** Waiter records a payment against their own tab */
  async waiterPayTab(
    tabId: string,
    amount: number,
    method: TabPayment["method"],
  ): Promise<Tab | null> {
    try {
      await tabsApi.pay(tabId, amount, method);
      await fetchWaiterTabs();
      return state.tabs.find((t) => t.id === tabId) ?? null;
    } catch {
      return null;
    }
  },

  /** Waiter closes their own tab. */
  async waiterCloseTab(tabId: string): Promise<Tab | null> {
    try {
      await tabsApi.close(tabId);
      await fetchWaiterTabs();
      return null;
    } catch {
      return null;
    }
  },

  /** Staff: add items to a specific customer's tab by tabId. */
  async staffAddToTab(
    tabId: string,
    itemId: string,
    name: string,
    qty: number,
    unitPrice: number,
  ): Promise<Tab | null> {
    try {
      await tabsApi.addItem(tabId, { itemId, name, qty, unitPrice });
      await fetchAllTabs();
      return state.tabs.find((t) => t.id === tabId) ?? null;
    } catch {
      return null;
    }
  },

  /** Staff: record a payment against a specific customer's tab. */
  async staffPayTab(
    tabId: string,
    amount: number,
    method: TabPayment["method"],
  ): Promise<Tab | null> {
    try {
      await tabsApi.pay(tabId, amount, method);
      await fetchAllTabs();
      return state.tabs.find((t) => t.id === tabId) ?? null;
    } catch {
      return null;
    }
  },

  /** Staff: close a specific customer's tab. */
  async staffCloseTab(tabId: string): Promise<Tab | null> {
    try {
      await tabsApi.close(tabId);
      await fetchAllTabs();
      return null;
    } catch {
      return null;
    }
  },

  // ── Cashier issuance ─────────────────────────────────────────────────────

  /**
   * Cashier issues (serves) items from a tab.
   * Creates POS sales entries and marks items as issued.
   */
  async issueItems(tabId: string): Promise<boolean> {
    try {
      await tabsApi.issue(tabId);
      await fetchAllTabs();
      return true;
    } catch {
      return false;
    }
  },

  /** Issue a single item from a tab. */
  async issueSingleItem(tabId: string, itemId: string): Promise<boolean> {
    try {
      await tabsApi.issueItem(tabId, itemId);
      await fetchAllTabs();
      return true;
    } catch {
      return false;
    }
  },

  reset: () => {
    state = initial;
    version++;
    persist();
  },
};

/** Subscribe to store changes using a version counter (stable primitive). */
function useStoreVersion(): number {
  return useSyncExternalStore(
    tabStore.subscribe,
    () => version,
    () => 0,
  );
}

/** React hook that returns the current open tab for the logged-in user. */
export function useCurrentTab(): Tab | null {
  const userId = authStore.getState().user?.id;
  useStoreVersion();
  return useMemo(
    () =>
      state.tabs.find((t) => t.customerId === userId && t.status === "open") ??
      null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version, userId],
  );
}

/** Generic hook to derive data from the tab store (stable by using version counter). */
export function useTabStore<T>(selector: (s: TabState) => T): T {
  const v = useStoreVersion();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => selector(state), [v]);
}
